import type { ConcurrentField } from '~~/errors/ConcurrentChangeError'

export type MergeRecord = Record<string, unknown>

export type ConflictChoice = 'mine' | 'current'

type ContactRow = { conLib: string; conChamp: string }

const FIELD_LABELS: Record<string, string> = {
  dirLib: 'Nom court',
  dirLibLong: 'Nom long',
  dirDesc: 'Description',
  dirType: 'Type',
  domLib: 'Acronyme du domaine',
  domLibLong: 'Nom du domaine',
  domDesc: 'Description',
  ageLib: "Acronyme de l'agence",
  ageLibLong: "Nom de l'agence",
  ageDesc: 'Description',
  equLib: "Nom de l'équipe",
  equDesc: 'Description',
  equMis: 'Mission',
}

export function buildConcurrentPatch(
  current: MergeRecord,
  original: MergeRecord,
  fields: readonly string[]
): { changes: MergeRecord; base: MergeRecord } | null {
  const changes: MergeRecord = {}
  const base: MergeRecord = {}
  for (const field of fields) {
    if (sameJson(current[field], original[field])) {
      continue
    }
    changes[field] = current[field]
    base[field] = original[field]
  }
  if (Object.keys(changes).length === 0) {
    return null
  }
  return { changes, base }
}

export function applyConflictChoices(
  form: MergeRecord,
  base: MergeRecord,
  fields: ConcurrentField[],
  choices: Record<string, ConflictChoice>
): void {
  for (const field of fields) {
    const choice = choices[field.field] ?? 'mine'
    if (field.field.startsWith('contacts.')) {
      applyContactChoice(form, base, field, choice)
      continue
    }
    form[field.field] = choice === 'current' ? field.current : field.mine
    base[field.field] = field.current
  }
}

export function fieldLabel(field: string): string {
  if (field.startsWith('contacts.')) {
    return `Contact ${field.slice('contacts.'.length)}`
  }
  return FIELD_LABELS[field] ?? field
}

export function formatConflictValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '(vide)'
  }
  return String(value)
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
}

function applyContactChoice(
  form: MergeRecord,
  base: MergeRecord,
  field: ConcurrentField,
  choice: ConflictChoice
): void {
  const label = field.field.slice('contacts.'.length)
  const chosen = choice === 'current' ? field.current : field.mine
  form.contacts = rewriteContact(asContacts(form.contacts), label, chosen)
  base.contacts = rewriteContact(asContacts(base.contacts), label, field.current)
}

function asContacts(value: unknown): ContactRow[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.flatMap(function toContact(row: unknown): ContactRow[] {
    if (row === null || typeof row !== 'object') {
      return []
    }
    const contact = row as ContactRow
    if (typeof contact.conLib !== 'string') {
      return []
    }
    return [{ conLib: contact.conLib, conChamp: contact.conChamp ?? '' }]
  })
}

function rewriteContact(contacts: ContactRow[], label: string, value: unknown): ContactRow[] {
  const remaining = contacts.filter(function keepOther(row: ContactRow): boolean {
    return row.conLib !== label
  })
  if (value === null || value === undefined) {
    return remaining
  }
  return [...remaining, { conLib: label, conChamp: String(value) }]
}
